// Getting a new release onto screens that never reload.
//
// The installed app (home-screen icon) is an SPA that can sit in memory for days: the browser
// looks for a new service worker only on a navigation, and even a new worker that installed keeps
// showing the old page until a reload. So:
//
//   - we ask the server for a new version ourselves every hour and each time the app comes back
//     to the foreground (at most once a minute); on start the browser's own check is enough;
//   - a found version is switched to silently only when nobody can be in the middle of something —
//     the first seconds after the page opened, or after returning from a long absence. A reload at
//     any other moment could drop a half-filled form, so then we only offer «Обновить».
//
// The service worker is built in `prompt` mode (vite.config.ts): a new worker waits until we tell
// it to take over, instead of replacing the old one under a running page.

/** Updates found this soon after the page opened are applied at once — nothing is typed yet. */
export const STARTUP_WINDOW_MS = 15_000
/** Away this long, a return to the app counts as a fresh start. */
export const LONG_ABSENCE_MS = 15 * 60_000
export const UPDATE_CHECK_INTERVAL_MS = 60 * 60_000
/** Returning to the app twice in a row should not hit the server twice. */
export const MIN_CHECK_GAP_MS = 60_000
/**
 * A second silent switch this soon in the same tab means the switch does not stick: on 17.10 Safari
 * 27 found the «new» worker again after every reload, and the page reloaded about once a second for
 * minutes. Then we only offer «Обновить» — one reload at most, and only when asked.
 */
export const AUTO_SWITCH_COOLDOWN_MS = 5 * 60_000
const AUTO_SWITCH_KEY = 'lupa:pwa-auto-switch-at'

/** The slice of `virtual:pwa-register` we use; injected so tests need no service worker. */
export type RegisterSW = (options: {
  immediate?: boolean
  onNeedRefresh?: () => void
  onRegisteredSW?: (swUrl: string, registration: ServiceWorkerRegistration | undefined) => void
}) => (reloadPage?: boolean) => Promise<void>

export type PwaUpdateEnvironment = {
  now: () => number
  isHidden: () => boolean
  onVisibilityChange: (listener: () => void) => void
  every: (ms: number, task: () => void) => void
  later: (ms: number, task: () => void) => void
  reload: () => void
  /** When this tab last switched by itself (0 if never); null when storage is blocked. */
  readAutoSwitchAt: () => number | null
  /** Remembers a silent switch across the reload it causes; false when storage is blocked. */
  writeAutoSwitchAt: (at: number) => boolean
}

/**
 * The plugin reloads when the new worker takes control of the page. A page opened on the very
 * first visit is not controlled by any worker, so that never happens and «Обновить» would do
 * nothing — reload ourselves if the page is still here after this long (found in a live check).
 */
export const RELOAD_FALLBACK_MS = 3_000

type Listener = () => void

let updateAvailable = false
let applyUpdate: (() => void) | null = null
const listeners = new Set<Listener>()

function setUpdateAvailable(value: boolean) {
  if (updateAvailable === value) return
  updateAvailable = value
  listeners.forEach((listener) => listener())
}

/** For useSyncExternalStore: whether a new version waits for the «Обновить» button. */
export function isPwaUpdateAvailable(): boolean {
  return updateAvailable
}

export function subscribePwaUpdate(listener: Listener): () => void {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

/** «Обновить»: let the waiting worker take over; the page reloads when it does. */
export function applyPwaUpdate(): void {
  applyUpdate?.()
}

const browserEnvironment: PwaUpdateEnvironment = {
  now: () => Date.now(),
  isHidden: () => document.visibilityState === 'hidden',
  onVisibilityChange: (listener) => document.addEventListener('visibilitychange', listener),
  every: (ms, task) => {
    window.setInterval(task, ms)
  },
  later: (ms, task) => {
    window.setTimeout(task, ms)
  },
  reload: () => window.location.reload(),
  readAutoSwitchAt: () => {
    try {
      return Number(sessionStorage.getItem(AUTO_SWITCH_KEY) ?? 0)
    } catch {
      return null
    }
  },
  writeAutoSwitchAt: (at) => {
    try {
      sessionStorage.setItem(AUTO_SWITCH_KEY, String(at))
      return true
    } catch {
      return false
    }
  },
}

export function startPwaUpdates(registerSW: RegisterSW, env: PwaUpdateEnvironment = browserEnvironment): void {
  // When the current stretch of use began: page load, or the return from a long absence.
  let sessionStartedAt = env.now()
  let hiddenAt: number | null = env.isHidden() ? env.now() : null
  let lastCheckAt = Number.NEGATIVE_INFINITY
  let registration: ServiceWorkerRegistration | undefined
  let waiting = false
  let applying = false

  const inQuietMoment = () => env.now() - sessionStartedAt <= STARTUP_WINDOW_MS

  // Storage blocked (private mode): nothing would stop a switch that does not stick from
  // reloading forever — so don't switch by ourselves at all, like lazyWithReload.
  const claimAutoSwitch = () => {
    const last = env.readAutoSwitchAt()
    if (last === null || env.now() - last < AUTO_SWITCH_COOLDOWN_MS) return false
    return env.writeAutoSwitchAt(env.now())
  }

  const apply = () => {
    if (applying) return
    applying = true
    void updateSW(true)
    env.later(RELOAD_FALLBACK_MS, env.reload)
  }

  const check = () => {
    if (!registration || env.now() - lastCheckAt < MIN_CHECK_GAP_MS) return
    lastCheckAt = env.now()
    // Offline or the server hiccuped — the next check will do.
    registration.update().catch(() => undefined)
  }

  const updateSW = registerSW({
    immediate: true,
    onNeedRefresh: () => {
      waiting = true
      if (inQuietMoment() && claimAutoSwitch()) apply()
      else setUpdateAvailable(true)
    },
    onRegisteredSW: (_swUrl, reg) => {
      registration = reg
      // No check of our own here: the browser has just fetched sw.js for this very page load. One
      // more fetch was a duplicate, and in Safari the lone `GET /sw.js 304` of a page load came on
      // a TLS connection of its own (log 27.10) — new connections in a burst are what trips
      // Beeline's 10-minute freeze (deploy/nginx/snippets/lupa-keepalive.conf). This moment counts
      // as a check; the next one is due on return to the app or in an hour.
      lastCheckAt = env.now()
    },
  })
  applyUpdate = apply

  env.onVisibilityChange(() => {
    if (env.isHidden()) {
      hiddenAt = env.now()
      return
    }
    const awayFor = hiddenAt === null ? 0 : env.now() - hiddenAt
    hiddenAt = null
    if (awayFor >= LONG_ABSENCE_MS) {
      sessionStartedAt = env.now()
      if (waiting && claimAutoSwitch()) {
        apply()
        return
      }
    }
    check()
  })
  env.every(UPDATE_CHECK_INTERVAL_MS, check)
}

/** Test-only: forget the module state between cases. */
export function resetPwaUpdatesForTest(): void {
  updateAvailable = false
  applyUpdate = null
  listeners.clear()
}
